import React, { useEffect, useRef } from "react";
import { Heart, DollarSign, ShoppingCart, GraduationCap } from "lucide-react";
import "./SupportedSectors.css";

const SupportedSectors = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: "0px 0px -50px 0px",
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("animate-in");
        }
      });
    }, observerOptions);

    const cards = document.querySelectorAll(".sector-card-v2");
    cards.forEach((card) => observer.observe(card));

    return () => {
      cards.forEach((card) => observer.unobserve(card));
    };
  }, []);

  const sectors = [
    {
      id: "healthcare",
      category: "Healthcare",
      title: "Healthcare",
      description:
        "Transforming patient care with innovative digital health solutions, telemedicine platforms, and AI-powered diagnostics.",
      icon: Heart,
      badgeBg: "#fef2f2",
      iconColor: "#ef4444",
      tagBg: "#ffffff",
      tagColor: "#ef4444",
      tagBorder: "#fca5a5",
      hoverBg: "#fff5f5",
      hoverAccent: "#ef4444",
      hoverBorder: "#fca5a5",
      tags: ["Telemedicine", "EHR Systems", "AI Diagnostics", "Patient Portals"],
    },
    {
      id: "financial",
      category: "Financial",
      title: "Financial",
      description:
        "Secure fintech solutions including digital banking, payment gateways, fraud detection, and blockchain technology.",
      icon: DollarSign,
      badgeBg: "#ecfdf5",
      iconColor: "#10b981",
      tagBg: "#ffffff",
      tagColor: "#10b981",
      tagBorder: "#6ee7b7",
      hoverBg: "#f0fdf4",
      hoverAccent: "#10b981",
      hoverBorder: "#6ee7b7",
      tags: ["Digital Banking", "Payment Systems", "Fraud Detection", "Blockchain"],
    },
    {
      id: "retail",
      category: "Retail",
      title: "Retail",
      description:
        "E-commerce platforms, inventory management, customer analytics, and omnichannel retail solutions.",
      icon: ShoppingCart,
      badgeBg: "#fffbeb",
      iconColor: "#f59e0b",
      tagBg: "#ffffff",
      tagColor: "#f59e0b",
      tagBorder: "#fde68a",
      hoverBg: "#fffdf0",
      hoverAccent: "#f59e0b",
      hoverBorder: "#fde68a",
      tags: ["E-commerce", "Inventory Management", "Analytics", "Omnichannel"],
    },
    {
      id: "edutech",
      category: "Edutech",
      title: "Edutech",
      description:
        "Learning management systems, virtual classrooms, student portals, and AI-powered personalized learning.",
      icon: GraduationCap,
      badgeBg: "#eff6ff",
      iconColor: "#3b82f6",
      tagBg: "#ffffff",
      tagColor: "#3b82f6",
      tagBorder: "#93c5fd",
      hoverBg: "#f0f7ff",
      hoverAccent: "#3b82f6",
      hoverBorder: "#93c5fd",
      tags: ["LMS Platforms", "Virtual Classrooms", "Student Portals", "AI Learning"],
    },
  ];

  return (
    <section className="supported-sectors-section" ref={sectionRef}>
      <div className="sectors-container">
        <div className="sectors-header section-header">
          <div className="sectors-label section-label">
            <svg
              className="sectors-label-icon"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z"
                fill="currentColor"
              />
            </svg>
            <span>Supported Sectors</span>
          </div>
          <h2 className="sectors-title section-title">Industries We Serve</h2>
          <p className="sectors-description section-description">
            We deliver cutting-edge technology solutions across diverse
            industries, helping businesses transform and thrive in the digital
            age.
          </p>
        </div>

        {/* 4 Cards in 1 Row - Big Size Square-Type Cards */}
        <div className="sectors-grid-v2">
          {sectors.map((sector) => {
            const SectorIcon = sector.icon;
            return (
              <div
                key={sector.id}
                className="sector-card-v2"
                style={{
                  "--hover-bg": sector.hoverBg,
                  "--hover-accent": sector.hoverAccent,
                  "--hover-border": sector.hoverBorder,
                }}
              >
                {/* Centered Top Icon Badge */}
                <div
                  className="sector-top-icon-badge"
                  style={{
                    backgroundColor: sector.badgeBg,
                    color: sector.iconColor,
                  }}
                >
                  <SectorIcon size={30} />
                </div>

                {/* Title & Description */}
                <h3 className="sector-card-title-v2">{sector.title}</h3>
                <p className="sector-card-desc-v2">{sector.description}</p>

                {/* Bottom Feature Tag Pills (Square Rounded Tags) */}
                <div className="sector-tags-row">
                  {sector.tags.map((tag, tagIndex) => (
                    <span
                      key={tagIndex}
                      className="sector-tag-pill"
                      style={{
                        backgroundColor: sector.tagBg,
                        color: sector.tagColor,
                        borderColor: sector.tagBorder,
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default SupportedSectors;
